import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Slider from "@mui/material/Slider";

import NotFound from "shared/components/UIElements/NotFound";
import MapSinglePoint from "components/shared/MapSinglePoint";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import {
  qrcodetypes_tr,
  qrcodetypes_en,
  yesNoOptions_tr,
  yesNoOptions_en,
  vehicleQRAttendanceTypes_tr,
  vehicleQRAttendanceTypes_en,
} from "shared/util/types";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { SCHOOL_TYPES, QR_CODE_TYPES, VEHICLE_QR_ATTENDANCE_TYPES } from "@routebot/constants";
import { AuthContext } from "shared/context/auth-context";
import { PERMISSION_TYPES } from "@routebot/constants";
import { getCurrentLanguage } from "shared/utility/lang";
import { isPhoneAppAdminUiEnabled } from "config/ConfigUtils";

const SCHOOL_DISTANCE_MIN_KM = 0.1;
const SCHOOL_DISTANCE_MAX_KM = 5;
const SCHOOL_DISTANCE_STEP_KM = 0.01;
const SCHOOL_DISTANCE_DEFAULT_KM = 0.5;

const clampSchoolDistance = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return SCHOOL_DISTANCE_DEFAULT_KM;
  }
  return Math.min(SCHOOL_DISTANCE_MAX_KM, Math.max(SCHOOL_DISTANCE_MIN_KM, numeric));
};

const getSchoolDistanceValue = (formState, loadedItem) =>
  clampSchoolDistance(formState.inputs.distance.value ?? loadedItem?.distance ?? SCHOOL_DISTANCE_DEFAULT_KM);

const SchoolItemBase = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem, config }) => {
  //standard code
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const currentLanguage = getCurrentLanguage();
  const qrcodetypes = currentLanguage === "tr" ? qrcodetypes_tr : qrcodetypes_en;
  const yesNoOptions = currentLanguage === "tr" ? yesNoOptions_tr : yesNoOptions_en;
  const vehicleQRAttendanceTypes = currentLanguage === "tr" ? vehicleQRAttendanceTypes_tr : vehicleQRAttendanceTypes_en;
  // context
  const { schools } = useContext(StoreContext);

  // item info
  const itemTypeLang = config?.isStudentSchool ? "studentSchoolItem" : "personelSchoolItem";
  const itemType = "school";
  const endPointUpdate = "schools";
  const endPointDelete = "schools";
  const endPointCreate = "schools";

  // item special states
  const [markerLocation, setMarkerLocation] = useState();
  const [schoolAddress, setSchoolAddress] = useState();
  const [addressInSync, setAddressInSync] = useState(false);

  // item special functions
  const mapDataChangeHandler = (address, location, inSync) => {
    setMarkerLocation(location);
    setSchoolAddress(address);
    setAddressInSync(inSync);
    inputHandler("address", address, true);
  };

  // formstates
  const [formState, inputHandler] = useForm(
    {
      name: { value: "", isValid: true },
      type: { value: "", isValid: true },
      useqrcode: { value: "", isValid: true },
      phonenumber: { value: "", isValid: true },
      email: { value: "", isValid: true },
      tag: { value: "", isValid: true },
      address: { value: null, isValid: true },
      location: { value: null, isValid: true },
      distance: { value: null, isValid: true },
      usemagneticcard: { value: false, isValid: true },
      sendReports: { value: false, isValid: true },
      showSupportPage: { value: false, isValid: true },
      vehicleQRAttendance: { value: "NOT_USED", isValid: true },
    },
    true,
  );

  // formstates fetch
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    const currentSchool = schools.find((school) => school.id === itemId);
    setLoadedItem(currentSchool);

    if (currentSchool?.location && currentSchool?.location.lat && currentSchool?.location.lng) {
      setMarkerLocation(currentSchool?.location);
      setAddressInSync(true);
    } else {
      setMarkerLocation(null);
      setAddressInSync(false);
    }

    setSchoolAddress(currentSchool?.address);

    setIsItemLoaded(true);
  }, [itemId, schools]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  useEffect(() => {
    if (!isItemLoaded || !auth?.permissions[PERMISSION_TYPES.ACCESS_TO_SERVISTESIN]) {
      return;
    }
    inputHandler("distance", clampSchoolDistance(loadedItem?.distance ?? SCHOOL_DISTANCE_DEFAULT_KM), true);
  }, [isItemLoaded, itemId, auth?.permissions, inputHandler]);

  // item update
  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        name: formState.inputs.name.value,
        tag: formState.inputs.tag.value,
        type: config?.isStudentSchool ? SCHOOL_TYPES.SCHOOL : SCHOOL_TYPES.PERSONEL,
        useqrcode:
          auth?.permissions[PERMISSION_TYPES.ACCESS_TO_SERVISTESIN] && config?.isStudentSchool
            ? formState.inputs.useqrcode.value
            : QR_CODE_TYPES.NOT_USED,
        phonenumber: formState.inputs.phonenumber.value,
        email: formState.inputs.email.value,
        distance: auth?.permissions[PERMISSION_TYPES.ACCESS_TO_SERVISTESIN]
          ? clampSchoolDistance(formState.inputs.distance.value)
          : 0.5,
        usemagneticcard: config?.isStudentSchool ? false : formState.inputs.usemagneticcard.value,
        sendReports: formState.inputs.sendReports.value,
        showSupportPage: formState.inputs.showSupportPage.value,
        vehicleQRAttendance: !config?.isStudentSchool
          ? formState.inputs.vehicleQRAttendance.value
          : VEHICLE_QR_ATTENDANCE_TYPES.NOT_USED,
        address: schoolAddress,
      };

      if (markerLocation) {
        infoToBeSent.location = { lat: markerLocation?.lat || 1.1, lng: markerLocation?.lng || 1.1 };
      }

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
        : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemTypeLang}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemTypeLang}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemTypeLang}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          <Input
            id="name"
            element="input"
            type="text"
            label={(config?.isStudentSchool ? t("studentSchoolOfficialName") : t("personelSchoolOfficialName")) + " *"}
            validators={[VALIDATOR_REQUIRE()]}
            errorText={t("requireField", { item: t("name") })}
            initialValue={loadedItem?.name}
            initialValid={isNewItem ? false : true}
            onInput={inputHandler}
          />

          <Input
            id="tag"
            element="input"
            type="text"
            label={(config?.isStudentSchool ? t("studentSchoolShortName") : t("personelSchoolShortName")) + " *"}
            validators={[VALIDATOR_REQUIRE()]}
            errorText={t("requireField", { item: t("name") })}
            initialValue={loadedItem?.tag}
            initialValid={isNewItem ? false : Boolean(loadedItem?.tag)}
            onInput={inputHandler}
          />

          <MapSinglePoint
            id="schoolItemMap"
            label={formState.inputs.name.value}
            initialAddress={schoolAddress || ""}
            initialAddressInSync={addressInSync}
            initialLocation={markerLocation}
            onInput={inputHandler}
            onMapDataChange={mapDataChangeHandler}
            // mapHeight="30rem"
            geofenceRadiusKm={
              auth?.permissions[PERMISSION_TYPES.ACCESS_TO_SERVISTESIN]
                ? getSchoolDistanceValue(formState, loadedItem)
                : undefined
            }
          ></MapSinglePoint>

          {auth?.permissions[PERMISSION_TYPES.ACCESS_TO_SERVISTESIN] && (
            <div className="generic-input">
              <label htmlFor="distance">{t("distanceToArriveSchool")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "8px" }}>
                <Slider
                  id="distance"
                  min={SCHOOL_DISTANCE_MIN_KM}
                  max={SCHOOL_DISTANCE_MAX_KM}
                  step={SCHOOL_DISTANCE_STEP_KM}
                  value={getSchoolDistanceValue(formState, loadedItem)}
                  onChange={(_, newValue) => inputHandler("distance", clampSchoolDistance(newValue), true)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${Number(value).toFixed(2)} km`}
                  aria-label={t("distanceToArriveSchool")}
                  sx={{ flex: 1, mx: 1 }}
                />
                <span style={{ minWidth: "4.5rem", textAlign: "right" }}>
                  {getSchoolDistanceValue(formState, loadedItem).toFixed(2)} km
                </span>
              </div>
              <p style={{ marginTop: "0.5rem", color: "gray", fontSize: "0.875rem" }}>
                {t("distanceMinMaxToArrivceSchool")}
              </p>
            </div>
          )}

          {config?.isStudentSchool && isPhoneAppAdminUiEnabled(auth.company?.features) && (
            <CustomSelect
              options={qrcodetypes}
              id="useqrcode"
              onInput={inputHandler}
              label={t("isQRCodeUsed")}
              validators={[VALIDATOR_REQUIRE()]}
              initialValue={
                qrcodetypes.find((option) => option.value.toString() === loadedItem?.useqrcode.toString()) ||
                qrcodetypes[1]
              }
            ></CustomSelect>
          )}

          {!config?.isStudentSchool && isPhoneAppAdminUiEnabled(auth.company?.features) && (
            <React.Fragment>
              <CustomSelect
                options={yesNoOptions}
                id="usemagneticcard"
                onInput={inputHandler}
                label={t("isMagneticCardsUsed")}
                validators={[VALIDATOR_REQUIRE()]}
                initialValue={
                  yesNoOptions.find((option) => option.value.toString() === loadedItem?.usemagneticcard.toString()) ||
                  yesNoOptions[1]
                }
              ></CustomSelect>

              <CustomSelect
                options={vehicleQRAttendanceTypes}
                id="vehicleQRAttendance"
                onInput={inputHandler}
                label={t("vehicleQRAttendance")}
                validators={[VALIDATOR_REQUIRE()]}
                initialValue={
                  vehicleQRAttendanceTypes.find((option) => option.value === loadedItem?.vehicleQRAttendance) ||
                  vehicleQRAttendanceTypes[0]
                }
                description={t("vehicleQRAttendanceDescription")}
              ></CustomSelect>
            </React.Fragment>
          )}

          {isPhoneAppAdminUiEnabled(auth.company?.features) && (
            <React.Fragment>
              <CustomSelect
                options={yesNoOptions}
                id="sendReports"
                onInput={inputHandler}
                label={t("sendReports")}
                validators={[VALIDATOR_REQUIRE()]}
                initialValue={
                  yesNoOptions.find(
                    (option) => option.value.toString() === Boolean(loadedItem?.sendReports)?.toString(),
                  ) || yesNoOptions[1]
                }
                description={t("sendReportsDescription")}
              ></CustomSelect>

              <Input
                id="email"
                element="input"
                type="text"
                label={t("emailForReports")}
                description={t("emailForReportsDescription")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.email || ""}
                initialValid={true}
                onInput={inputHandler}
              />

              <Input
                id="phonenumber"
                element="input"
                type="text"
                label={t("phoneNumberForApp")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.phonenumber || ""}
                initialValid={true}
                onInput={inputHandler}
                description={t("phoneNumberForAppDescription")}
              />

              <CustomSelect
                options={yesNoOptions}
                id="showSupportPage"
                onInput={inputHandler}
                label={t("showSupportPage")}
                validators={[VALIDATOR_REQUIRE()]}
                initialValue={
                  yesNoOptions.find(
                    (option) => option.value.toString() === Boolean(loadedItem?.showSupportPage)?.toString(),
                  ) || yesNoOptions[1]
                }
                description={t("showSupportPageDescription")}
              ></CustomSelect>
            </React.Fragment>
          )}

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={!formState.isValid || !addressInSync || isLoading}
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default SchoolItemBase;
